import React, { useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
  Avatar,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const SuperAdminLoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // For super admin login, we don't need tenant selection
      const success = await login(username, password);
      if (success) {
        // Redirect based on role
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          if (user.role === 'SUPER_ADMIN') {
            navigate('/dashboard');
          } else if (user.role === 'CLIENT_ADMIN') {
            navigate('/client/dashboard');
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('Invalid username or password');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
        }}>
          <CardContent sx={{ pt: 4, pb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Avatar sx={{
                width: 60,
                height: 60,
                fontSize: 32,
                bgcolor: 'secondary.main',
                color: 'white'
              }}>
                <SecurityIcon />
              </Avatar>
            </Box>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Healthcare Platform - Admin Portal
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Sign in with super admin or client admin credentials
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                helperText="Use 'superadmin' or 'clientadmin' for demo access"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Use any password for demo access"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 'bold' }}
              >
                Sign In as Admin
              </Button>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Facility Login
                  </Link>
                </Grid>
                <Grid item>
                  <Link href="#" variant="body2">
                    Forgot password
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
          © {new Date().getFullYear()} Healthcare Platform. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default SuperAdminLoginPage;